import React from "react";
import Subtitle from "../components/typography/Subtitle";
import Title from "../components/typography/Title";
import Description from "../components/typography/Description";
import Button from "../shared/button/Button";
import JoinUs from "../components/JoinUs";
import {
  ArrowUpRightIcon,
  ArrowRightIcon,
  CodeIcon,
  WebhooksLogoIcon,
  ShieldCheckIcon,
  GlobeIcon,
  LightningIcon,
  BracketsSquareIcon,
  MicrophoneIcon,
  ChartBarIcon,
  LockKeyIcon,
  BookOpenTextIcon,
  UsersThreeIcon,
  GraduationCapIcon,
  BuildingsIcon,
  BriefcaseIcon,
} from "@phosphor-icons/react";

/* ────────────────────────── Accent palette ────────────────────────── */

const accentPalette = {
  pageBg: "#071C22",
  panelBg: "#0A2630",
  acc: "#00E5FF",
  accA: "#00E5FF",
  accB: "#00B4D8",
  glow: "rgba(0,229,255,0.35)",
  border: "rgba(0,229,255,0.22)",
};

const cssVars: React.CSSProperties = {
  ["--pageBg" as string]: accentPalette.pageBg,
  ["--panelBg" as string]: accentPalette.panelBg,
  ["--acc" as string]: accentPalette.acc,
  ["--accA" as string]: accentPalette.accA,
  ["--accB" as string]: accentPalette.accB,
  ["--accGlow" as string]: accentPalette.glow,
  ["--accBorder" as string]: accentPalette.border,
} as React.CSSProperties;

/* ────────────────────────── Data ────────────────────────── */

const whatIsIt = [
  {
    icon: MicrophoneIcon,
    title: "Conduct Voice AI Interviews",
    description:
      "Interview candidates using natural voice AI with simple API calls that handle complex speech and conversation logic for you.",
  },
  {
    icon: ChartBarIcon,
    title: "Get In-Depth Analysis",
    description:
      "Transform interview responses into meaningful insights with endpoints that understand and score what candidates actually say.",
  },
  {
    icon: LockKeyIcon,
    title: "Secure & Easy to Implement",
    description:
      "Add interview capabilities to your app with clear documentation and ready examples — all secured with enterprise-grade encryption.",
  },
];

const keyFeatures = [
  {
    icon: LightningIcon,
    title: "Effortless Development",
    description:
      "Create an intelligent AI interviewer in just a few lines of code. Scale to thousands of concurrent interviews overnight.",
    accent: "#00E5FF",
    large: true,
  },
  {
    icon: GlobeIcon,
    title: "Multilingual Interviews",
    description:
      "Conduct interviews in 20+ languages including English, Hindi, Spanish, French, and Arabic — reach talent globally.",
    accent: "#FDBB30",
    large: false,
  },
  {
    icon: WebhooksLogoIcon,
    title: "Real-Time Webhooks",
    description:
      "Receive instant callbacks when interviews complete, candidates are scored, or assessments finish. Keep your systems perfectly in sync.",
    accent: "#FF505F",
    large: false,
  },
  {
    icon: BracketsSquareIcon,
    title: "Structured Evaluation Data",
    description:
      "Get detailed JSON responses with candidate scores, skill breakdowns, transcript analysis, and AI-powered hire/no-hire recommendations.",
    accent: "#C77DFF",
    large: true,
  },
  {
    icon: ShieldCheckIcon,
    title: "Enterprise-Grade Security",
    description:
      "SOC 2 Type II compliant, ISO 27001 certified, GDPR ready. Your data stays encrypted at rest and in transit.",
    accent: "#14AE5C",
    large: false,
  },
  {
    icon: CodeIcon,
    title: "Fully White-Labelled",
    description:
      "Integrate via API or resell with Babblebots' UI under your own brand. Complete white-label support included.",
    accent: "#7AA7FF",
    large: false,
  },
];

const endpoints = [
  {
    method: "POST",
    path: "/v1/interviews",
    description: "Create a new AI interview session",
  },
  {
    method: "GET",
    path: "/v1/interviews/:id",
    description: "Retrieve interview details & status",
  },
  {
    method: "GET",
    path: "/v1/interviews/:id/report",
    description: "Get detailed evaluation report",
  },
  {
    method: "POST",
    path: "/v1/assessments",
    description: "Create a coding or skill assessment",
  },
  {
    method: "POST",
    path: "/v1/screen",
    description: "AI phone screening for a candidate",
  },
  {
    method: "GET",
    path: "/v1/webhooks",
    description: "List & manage webhook subscriptions",
  },
];

const useCasesApi = [
  {
    icon: BriefcaseIcon,
    title: "Recruitment Platforms",
    description:
      "Embed AI interviews directly into your ATS or job board. Offer automated screening as a native feature to your customers.",
    accent: "#00E5FF",
  },
  {
    icon: UsersThreeIcon,
    title: "Staffing Agencies",
    description:
      "Scale candidate evaluation across hundreds of open roles without adding headcount. Process thousands of applicants simultaneously.",
    accent: "#FF505F",
  },
  {
    icon: GraduationCapIcon,
    title: "EdTech & Universities",
    description:
      "Assess student readiness, conduct placement drives, and offer mock interview prep — all powered by API.",
    accent: "#FDBB30",
  },
  {
    icon: BuildingsIcon,
    title: "Enterprise HR Tech",
    description:
      "Integrate AI-driven hiring into your internal HR suite. Automate first-round interviews for every department.",
    accent: "#C77DFF",
  },
];

/* ────────────────────────── Helpers ────────────────────────── */

const MethodBadge = ({ method }: { method: string }) => {
  const colors: Record<string, string> = {
    GET: "bg-green-500/15 text-green-400 border-green-500/20",
    POST: "bg-blue-500/15 text-blue-400 border-blue-500/20",
    PUT: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
    DELETE: "bg-red-500/15 text-red-400 border-red-500/20",
  };
  return (
    <span
      className={`inline-block rounded border px-2.5 py-1 font-mono text-xs font-bold ${colors[method] || "border-gray-600 bg-gray-700 text-gray-300"}`}
    >
      {method}
    </span>
  );
};

/* ────────────────────────── Page ────────────────────────── */

const ApiPage = () => {
  return (
    <main
      style={cssVars}
      className="relative min-h-screen overflow-hidden bg-[var(--pageBg)] text-white"
    >
      {/* ═══════════ 1. HERO + USE CASES ═══════════ */}
      <section className="relative flex w-full flex-col items-center px-4 pb-20 pt-12 md:pb-28 md:pt-20">
        {/* Ambient glow */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(900px 520px at 18% 28%, var(--accGlow), transparent 60%),
              radial-gradient(760px 420px at 82% 72%, rgba(255,255,255,0.07), transparent 62%)
            `,
          }}
        />
        <Subtitle>Babblebots API</Subtitle>
        <Title as="h1" size="xl" className="mt-4 max-w-screen-md">
          Build AI-Powered Hiring Into Your Product
        </Title>

        {/* Who builds with Babblebots API */}
        <div className="relative mx-auto mt-12 w-full max-w-screen-md md:mt-16">
          <h2 className="text-center font-serif text-lg font-medium text-white/70 md:text-xl">
            Who Builds With Babblebots API?
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {useCasesApi.map((uc) => (
              <div
                key={uc.title}
                className="group flex items-start gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 transition-all duration-300 hover:border-[var(--accBorder)] hover:bg-white/[0.05]"
              >
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${uc.accent}15` }}
                >
                  <uc.icon
                    size={22}
                    weight="duotone"
                    style={{ color: uc.accent }}
                  />
                </div>
                <div>
                  <h3 className="font-serif text-base font-medium text-white">
                    {uc.title}
                  </h3>
                  <p className="mt-1.5 font-sans text-sm leading-relaxed text-gray-400">
                    {uc.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a href="https://app.babblebots.ai" target="_blank" rel="noreferrer">
            <Button
              variant="primary"
              size="lg"
              icon={<ArrowUpRightIcon size={18} />}
              className="flex-row-reverse"
            >
              Get Your API Key
            </Button>
          </a>
          <a
            href="https://magenta-ladybug-395.notion.site/Babblebots-External-Partner-Integration-b41f64a7011a4657829bada2ec484e53"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="secondary"
              size="lg"
              icon={<BookOpenTextIcon size={18} />}
              className="flex-row-reverse"
            >
              Read Docs
            </Button>
          </a>
        </div>
      </section>

      {/* ═══════════ 3. WHAT IS IT ═══════════ */}
      <section className="relative w-full px-4 py-16 md:py-24">
        <div className="mx-auto max-w-screen-lg">
          <div className="text-center">
            <Subtitle>Babblebots API</Subtitle>
            <Title as="h2" className="mx-auto mt-4 max-w-screen-md">
              What Is It?
            </Title>
            <Description className="mx-auto mt-4 max-w-screen-sm">
              Babblebots API lets you conduct AI-driven interviews across any
              use case — recruitment, assessments, phone screening, and more.
              Use your imagination.
            </Description>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {whatIsIt.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-7 transition-all duration-300 hover:border-[var(--accBorder)] hover:bg-white/[0.05]"
              >
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${accentPalette.acc}15` }}
                >
                  <item.icon
                    size={26}
                    weight="duotone"
                    className="text-[var(--acc)]"
                  />
                </div>
                <h3 className="mt-5 font-serif text-lg font-medium text-white">
                  {item.title}
                </h3>
                <p className="mt-2 font-sans text-sm leading-relaxed text-gray-400">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 3. KEY FEATURES (Bento Grid) ═══════════ */}
      <section className="relative w-full px-4 py-16 md:py-24">
        <div className="mx-auto max-w-screen-lg text-center">
          <Subtitle>What's Included</Subtitle>
          <Title as="h2" className="mt-4">
            Key Features
          </Title>
          <Description className="mx-auto mt-4 max-w-screen-sm">
            Powerful building blocks that give developers complete control while
            handling the complex AI work behind the scenes.
          </Description>
        </div>
        <div className="mx-auto mt-12 grid w-full max-w-screen-lg grid-cols-1 gap-5 md:grid-cols-2">
          {keyFeatures.map((f) => (
            <div
              key={f.title}
              className="group relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03] p-7 transition-all duration-300 hover:border-[var(--accBorder)] hover:bg-white/[0.05]"
            >
              {/* Hover glow */}
              <div
                className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
                style={{ backgroundColor: `${f.accent}18` }}
              />
              <div className="relative z-10">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${f.accent}15` }}
                >
                  <f.icon
                    size={24}
                    weight="duotone"
                    style={{ color: f.accent }}
                  />
                </div>
                <h3 className="mt-5 font-serif text-lg font-medium text-white">
                  {f.title}
                </h3>
                <p className="mt-2 font-sans text-sm leading-relaxed text-gray-400">
                  {f.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════ 5. API ENDPOINTS ═══════════ */}
      <section className="relative w-full bg-[var(--panelBg)] px-4 py-16 md:py-24">
        <div className="mx-auto max-w-screen-lg text-center">
          <Subtitle>API Reference</Subtitle>
          <Title as="h2" className="mt-4">
            Core Endpoints
          </Title>
          <Description className="mx-auto mt-4 max-w-screen-sm">
            A RESTful interface designed for simplicity. Authenticate with your
            API key and start making calls immediately.
          </Description>
        </div>
        <div className="mx-auto mt-12 w-full max-w-screen-md overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03]">
          {endpoints.map((ep, i) => (
            <div
              key={ep.path + ep.method}
              className={`flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-center sm:gap-4 ${
                i !== endpoints.length - 1 ? "border-b border-white/[0.06]" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <MethodBadge method={ep.method} />
                <code className="font-mono text-sm text-white">{ep.path}</code>
              </div>
              <span className="text-sm text-gray-500 sm:ml-auto">
                {ep.description}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <a
            href="https://magenta-ladybug-395.notion.site/Babblebots-External-Partner-Integration-b41f64a7011a4657829bada2ec484e53"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 font-serif text-sm font-medium text-[var(--acc)] transition-colors hover:text-white"
          >
            View Full Documentation
            <ArrowRightIcon
              size={14}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
        </div>
      </section>

      <JoinUs />
    </main>
  );
};

export default ApiPage;
